import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  Activity,
  Brain,
  ClipboardList,
  Droplet,
  Eye,
  FileHeart,
  History,
  Loader2,
  ShieldCheck,
  Sparkles,
  Trash2,
  Utensils,
  X,
} from "lucide-react";
import RequireAuth from "@/components/silentscan/RequireAuth";
import { useAuth } from "@/context/AuthContext";
import { useChatbot } from "@/context/ChatbotContext";
import {
  listRecords,
  getRecord,
  deleteRecord,
  deleteAllRecords,
  deleteAccount,
} from "@/lib/records";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const TYPE_META = {
  health_score: { label: "Health Score", color: "#22d3ee", icon: FileHeart },
  mental: { label: "Mental Wellness", color: "#a78bfa", icon: Brain },
  food: { label: "Food AI", color: "#34d399", icon: Utensils },
  doctor_report: { label: "Doctor's Report", color: "#fbbf24", icon: ClipboardList },
  glucose_reading: { label: "Blood Glucose", color: "#fb7185", icon: Droplet },
  diabetes_plan: { label: "Diabetes Plan", color: "#10b981", icon: ClipboardList },
  rehab_progress: { label: "Rehab Progress", color: "#38bdf8", icon: Activity },
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
};
const fmtTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
};

export default function HealthHistory() {
  return (
    <RequireAuth title="Your health history is private">
      <HistoryInner />
    </RequireAuth>
  );
}

function HistoryInner() {
  const { user, logout } = useAuth();
  const { openChatbot } = useChatbot();
  const [records, setRecords] = useState(null);
  const [openRec, setOpenRec] = useState(null); // full record in modal
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // record card
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [confirmDeleteAccount, setConfirmDeleteAccount] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setRecords(await listRecords());
    } catch {
      toast.error("Could not load your history");
      setRecords([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openFull = async (rec) => {
    setLoadingDetail(true);
    try {
      setOpenRec(await getRecord(rec.record_id));
    } catch {
      toast.error("Could not open this record");
    } finally {
      setLoadingDetail(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      await deleteRecord(confirmDelete.record_id);
      toast.success("Record permanently deleted");
      setConfirmDelete(null);
      await load();
    } catch {
      toast.error("Could not delete the record");
    } finally {
      setBusy(false);
    }
  };

  const doDeleteAll = async () => {
    setBusy(true);
    try {
      await deleteAllRecords();
      toast.success("Your entire health history was permanently deleted");
      setConfirmDeleteAll(false);
      await load();
    } catch {
      toast.error("Could not delete history");
    } finally {
      setBusy(false);
    }
  };

  const doDeleteAccount = async () => {
    setBusy(true);
    try {
      await deleteAccount();
      toast.success("Your account and all data were permanently deleted");
      window.location.href = "/";
    } catch {
      toast.error("Could not delete the account");
      setBusy(false);
    }
  };

  return (
    <div data-testid="health-history-page" className="min-h-screen bg-[#05050A] text-white">
      <div className="max-w-3xl mx-auto px-6 pt-24 pb-16">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to dashboard
        </Link>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-cyan-400 font-semibold">
              <History className="w-3.5 h-3.5" />
              My Health History
            </div>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Your previous results
            </h1>
            <p className="mt-1 text-sm text-zinc-500">
              Signed in as {user?.email}. Only you can see this page.
            </p>
          </div>
          {records?.length > 0 && (
            <button
              data-testid="delete-all-history-btn"
              onClick={() => setConfirmDeleteAll(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-rose-400/30 bg-rose-500/10 text-xs text-rose-300 hover:bg-rose-500/20 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete all history
            </button>
          )}
        </div>

        {/* Records */}
        {records === null ? (
          <div className="mt-16 flex justify-center">
            <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
          </div>
        ) : records.length === 0 ? (
          <div
            data-testid="history-empty"
            className="mt-10 rounded-3xl border border-white/10 bg-white/[0.03] p-10 text-center"
          >
            <p className="text-sm text-zinc-400">
              No saved results yet. Complete a health check-in, mental wellness
              scan, food analysis or use the Diabetes Centre — each result is
              saved here automatically while you&apos;re signed in.
            </p>
            <button
              data-testid="history-start-btn"
              onClick={openChatbot}
              className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full btn-glow text-white text-sm font-medium"
            >
              <Sparkles className="w-4 h-4" />
              Start free health check-in
            </button>
          </div>
        ) : (
          <div className="mt-8 space-y-3" data-testid="history-list">
            {records.map((r, i) => {
              const meta = TYPE_META[r.type] || TYPE_META.health_score;
              const Icon = meta.icon;
              return (
                <motion.div
                  key={r.record_id}
                  data-testid={`history-record-${r.record_id}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.03, 0.4), duration: 0.35 }}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 flex items-center gap-4"
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border"
                    style={{ background: `${meta.color}12`, borderColor: `${meta.color}35` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: meta.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-zinc-500">
                      {fmtDate(r.created_at)} · {fmtTime(r.created_at)}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-white">{r.title || meta.label}</span>
                      <span
                        className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest border"
                        style={{ color: meta.color, borderColor: `${meta.color}40`, background: `${meta.color}10` }}
                      >
                        {meta.label}
                      </span>
                      {r.score != null && (
                        <span className="font-display text-base font-semibold" style={{ color: meta.color }}>
                          {r.score}
                          {r.type === "health_score" || r.type === "mental" ? "%" : ""}
                        </span>
                      )}
                    </div>
                    {r.summary && (
                      <p className="mt-1 text-xs text-zinc-500 truncate">{r.summary}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      data-testid={`view-record-${r.record_id}`}
                      onClick={() => openFull(r)}
                      className="w-9 h-9 rounded-full flex items-center justify-center bg-white/[0.04] border border-white/10 text-zinc-400 hover:text-cyan-300 hover:border-cyan-400/40 transition-colors"
                      aria-label="Open full result"
                    >
                      {loadingDetail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      data-testid={`delete-record-${r.record_id}`}
                      onClick={() => setConfirmDelete(r)}
                      className="w-9 h-9 rounded-full flex items-center justify-center bg-white/[0.04] border border-white/10 text-zinc-400 hover:text-rose-300 hover:border-rose-400/40 transition-colors"
                      aria-label="Delete record"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Privacy & Data */}
        <div
          data-testid="privacy-data-section"
          className="mt-12 rounded-3xl border border-white/10 bg-white/[0.03] p-6"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
            Privacy &amp; Data
          </div>
          <h2 className="mt-2 font-display text-xl font-semibold">What we store about you</h2>
          <ul className="mt-3 space-y-1.5 text-xs text-zinc-400 leading-relaxed list-disc pl-4">
            <li>Your account: name, email and sign-in method.</li>
            <li>Your health records: scores, results, doctor reports and glucose readings — stored securely, tied to your account ID, visible only to you.</li>
            <li>Records are never written to plain browser storage, never put in links, and never shared with other users.</li>
          </ul>
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <button
              data-testid="delete-account-btn"
              onClick={() => setConfirmDeleteAccount(true)}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-rose-400/40 bg-rose-500/10 text-xs font-semibold text-rose-300 hover:bg-rose-500/20 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete my account &amp; all data
            </button>
            <button
              data-testid="history-signout-btn"
              onClick={logout}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-white/15 bg-white/[0.04] text-xs text-zinc-300 hover:text-white transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Full record modal */}
      {openRec && (
        <div
          data-testid="record-detail-modal"
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
          onClick={() => setOpenRec(null)}
        >
          <div
            className="w-full max-w-lg max-h-[80vh] overflow-y-auto rounded-3xl border border-white/10 bg-[#0A0A14] p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs text-zinc-500">
                  {fmtDate(openRec.created_at)} · {fmtTime(openRec.created_at)}
                </div>
                <h3 className="mt-1 font-display text-xl font-semibold text-white">
                  {openRec.title}
                </h3>
              </div>
              <button
                data-testid="record-detail-close"
                onClick={() => setOpenRec(null)}
                className="w-8 h-8 rounded-full flex items-center justify-center bg-white/[0.04] border border-white/10 text-zinc-400 hover:text-white"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {openRec.score != null && (
              <div className="mt-4 font-display text-4xl font-semibold text-cyan-300">
                {openRec.score}
                {(openRec.type === "health_score" || openRec.type === "mental") && (
                  <span className="text-lg text-zinc-500">/100</span>
                )}
              </div>
            )}
            {openRec.summary && (
              <p className="mt-3 text-sm text-zinc-300 leading-relaxed">{openRec.summary}</p>
            )}
            <div className="mt-4">
              <PayloadView payload={openRec.payload} />
            </div>
          </div>
        </div>
      )}

      {/* Confirm: delete one record */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="confirm-delete-dialog" className="bg-[#0A0A14] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this health record?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to permanently delete this health record?
              {confirmDelete ? ` (${confirmDelete.title} — ${fmtDate(confirmDelete.created_at)})` : ""}{" "}
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel" className="bg-white/5 border-white/10 text-white hover:bg-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-confirm"
              onClick={doDelete}
              disabled={busy}
              className="bg-rose-500 text-white hover:bg-rose-600"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm: delete all history */}
      <AlertDialog open={confirmDeleteAll} onOpenChange={setConfirmDeleteAll}>
        <AlertDialogContent data-testid="confirm-delete-all-dialog" className="bg-[#0A0A14] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your entire history?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to permanently delete all your health records,
              glucose readings, doctor reports and plans? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-all-confirm"
              onClick={doDeleteAll}
              disabled={busy}
              className="bg-rose-500 text-white hover:bg-rose-600"
            >
              Delete everything
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm: delete account */}
      <AlertDialog open={confirmDeleteAccount} onOpenChange={setConfirmDeleteAccount}>
        <AlertDialogContent data-testid="confirm-delete-account-dialog" className="bg-[#0A0A14] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your account?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure? Your account, all health records, glucose readings,
              doctor reports and plans will be permanently deleted. This cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-account-confirm"
              onClick={doDeleteAccount}
              disabled={busy}
              className="bg-rose-500 text-white hover:bg-rose-600"
            >
              Delete my account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Generic readable renderer for a record's full payload.
const PayloadView = ({ payload }) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
  const SKIP = new Set(["user_id", "record_id", "created_at", "summary", "score", "saved_at"]);
  const entries = Object.entries(payload).filter(
    ([k, v]) => !SKIP.has(k) && v != null && v !== "" && !(Array.isArray(v) && v.length === 0)
  );
  if (!entries.length) return null;
  const label = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <div className="space-y-3">
      {entries.slice(0, 14).map(([k, v]) => (
        <div key={k} className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
            {label(k)}
          </div>
          {Array.isArray(v) ? (
            <ul className="mt-1.5 space-y-1 text-xs text-zinc-300 list-disc pl-4">
              {v.slice(0, 10).map((item, i) => (
                <li key={i}>
                  {typeof item === "object"
                    ? item.problem || item.title || item.text || JSON.stringify(item)
                    : String(item)}
                </li>
              ))}
            </ul>
          ) : typeof v === "object" ? (
            <div className="mt-1.5 space-y-1">
              {Object.entries(v).slice(0, 8).map(([k2, v2]) => (
                <div key={k2} className="text-xs text-zinc-300">
                  <span className="text-zinc-500">{label(k2)}: </span>
                  {typeof v2 === "object" ? JSON.stringify(v2) : String(v2)}
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-1.5 text-xs text-zinc-300 leading-relaxed">{String(v)}</p>
          )}
        </div>
      ))}
    </div>
  );
};
